import {
  Box,
  Button,
  Chip,
  Container,
  Drawer,
  Typography,
  useMediaQuery,
} from '@mui/material'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import {
  TableBodyCell,
  TableBodyRow,
} from '../../../components/styled/Table.styled'
import AvatarTableCell from '../../../components/table/AvatarTableCell'
import RestaurantTable from '../../../components/table/RestaurantTable'
import TextTableCell from '../../../components/table/TextTableCell'
import { WEB_NAV_HEIGHT } from '../../../helpers/static-constants'
import { useAppSelector } from '../../../store'
import { selectReservations } from '../../../store/reservation'
import {
  IReservation,
  ReservationStatus,
} from '../../../types/reservationTypes'
import { CONTENT_WIDTH, DRAWER_WIDTH } from '../coupons/Coupons'
import ReservationDetail from './components/ReservationDetail'
import ChatDrawer from '../../../components/restaurant/ChatDrawer'
import EmptyScreen from '../../../components/EmptyScreen'
import CancelReservation from './components/CancelReservation'

const Reservations: React.FC = () => {
  const reservations = useAppSelector(selectReservations)
  const isBetween960And1300 = useMediaQuery(
    '(min-width:960px) and (max-width:1300px)',
  )
  const [width, setWidth] = useState(window.innerWidth)
  const [openCouponDetailDrawer, setOpenCouponDetailDrawer] = useState(false)
  const [reservation, setReservation] = useState<IReservation | undefined>()
  const [openDrawer, setOpenDrawer] = useState(false)
  const [openCancelReservationDrawer, setOpenCancelReservationDrawer] =
    useState(false)

  useEffect(() => {
    const handleResize = (): void => setWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const onViewReservation = (reservation: IReservation): void => {
    setReservation(reservation)
    setOpenCouponDetailDrawer(true)
  }
  return (
    <Box
      height="100%"
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Helmet>
        <title>Resactly | My Reservations</title>
      </Helmet>
      <Container
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          flex={1}
          sx={{
            minHeight: 0,
            width: {
              md: openCouponDetailDrawer
                ? isBetween960And1300
                  ? `calc(100% - ${DRAWER_WIDTH}px)`
                  : `calc(100% - ${
                      DRAWER_WIDTH - (width - CONTENT_WIDTH - 10) / 2
                    }px)`
                : '100%',
            },
            willChange: 'width',
            transition: 'width 300ms cubic-bezier(0.25, 0.1, 0.25, 1)',
          }}
        >
          <Typography py={{ xs: 3, md: 5 }} variant="h3" flexShrink={0}>
            Reservations
          </Typography>
          <Box
            sx={{
              flexGrow: 1,
              minHeight: 0,
              overflowY: 'auto',
              maxHeight: '465px',
              pr: 1,
            }}
          >
            {reservations.length > 0 ? (
              <RestaurantTable
                noBorder
                maxHeight={'100%'}
                data={reservations ?? []}
                renderRow={(row, index) => (
                  <TableBodyRow
                    key={index}
                    sx={{
                      backgroundColor:
                        row.id === reservation?.id ? 'secondary.50' : '',
                    }}
                  >
                    <AvatarTableCell
                      imageUrl={row.restaurant.imageUrl}
                      primaryText={row.restaurant.name}
                      secondaryText={row.restaurant.description}
                      imgSize={40}
                      imgRadius="12px"
                    />

                    <TextTableCell
                      align="left"
                      text={
                        row?.reservationDate && row?.time
                          ? dayjs(
                              `${row?.reservationDate} ${row?.time}`,
                            ).format('D MMMM YYYY, h:mm A')
                          : '-'
                      }
                      maxWidth="300px"
                      sx={{
                        py: '12px !important',
                      }}
                    />
                    <TextTableCell
                      text={row?.guests ? `${row?.guests} Guests` : '-'}
                      minWidth="90px"
                    />
                    <TableBodyCell
                      sx={{
                        py: '12px !important',
                      }}
                    >
                      <Chip
                        label={row.status.toLocaleLowerCase()}
                        sx={{
                          textTransform: 'capitalize',
                          color: 'common.white',
                          backgroundColor:
                            row.status === ReservationStatus.COMPLETED
                              ? 'success.500'
                              : row.status === ReservationStatus.CANCELLED
                              ? 'grey.500'
                              : 'warning.500',
                          width: '95px',
                        }}
                      />
                    </TableBodyCell>
                    <TableBodyCell
                      align="right"
                      sx={{
                        width: '202px',
                        py: '12px !important',
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => onViewReservation(row)}
                      >
                        View Reservation
                      </Button>
                    </TableBodyCell>
                  </TableBodyRow>
                )}
              />
            ) : (
              <EmptyScreen
                mainText="No Reservations Found"
                secondaryText="You don’t have any upcoming, completed, or cancelled reservations yet."
                imageUrl="/images/empty-screen.svg"
                containerHeight={'400px'}
              />
            )}
          </Box>
        </Box>
      </Container>
      <Drawer
        variant="persistent"
        open={openCouponDetailDrawer}
        anchor="right"
        sx={{
          flexShrink: 0,
          width: { xs: '100vw', md: openCouponDetailDrawer ? DRAWER_WIDTH : 0 },
          willChange: 'width',
          transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          '& .MuiDrawer-paper': {
            width: { xs: '100vw', md: DRAWER_WIDTH },
            marginTop: {
              xs: `0px`,
              md: `${WEB_NAV_HEIGHT}px`,
            },
            height: {
              xs: '100vh',
              md: `calc(100vh - ${WEB_NAV_HEIGHT}px)`,
            },
          },
        }}
      >
        {reservation && (
          <ReservationDetail
            reservation={reservation}
            onClose={() => {
              setReservation(undefined)
              setOpenCouponDetailDrawer(false)
            }}
            openChat={() => setOpenDrawer(true)}
            onCancel={() => setOpenCancelReservationDrawer(true)}
          />
        )}
      </Drawer>
      <ChatDrawer
        open={openDrawer}
        toggleDrawer={setOpenDrawer}
        restaurantInfo={reservation?.restaurant}
      />
      <CancelReservation
        open={openCancelReservationDrawer}
        handleClose={() => setOpenCancelReservationDrawer(false)}
        onCancel={() => setOpenCancelReservationDrawer(false)}
        onHandleConfirm={(reason: string) => {
          setOpenCancelReservationDrawer(false)
        }}
      />
    </Box>
  )
}

export default Reservations
